import { CloudArrowUpIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState, useCallback, ReactNode } from 'react';
import { DropzoneProps, FileRejection, useDropzone } from 'react-dropzone';
import { Button } from '../Button/Button';
import { useTranslation } from 'react-i18next';
import { LoaderResult } from './types';
import { classNames } from 'support/helpers/generic/generic';
import { FieldError } from 'react-hook-form';
import { Input } from '../Input/Input/Input';
import { LoadingLogo } from 'components/Loading/LoadingLogo';

const classes = {
  error: 'border-red-300',
  default: 'border-gray-300',
  dragActive: 'border-procuros-green-300 bg-procuros-green-50',
  loading: 'border-0 bg-gray-100 text-gray-300',
};

export enum FileInputErrorCodes {
  INVALID_FILE_TYPE = 'file-invalid-type',
}
export type FileInputProps<T = any> = {
  onLoad: (data: any, file: File) => void;
  onError: (error: Error, file: File) => void;
  onClear?: () => void;
  validate?: (data: T) => { success: false; errorCode: string } | { success: true };
  accept?: DropzoneProps['accept'];
  label?: string;
  loadingLabel?: string | ReactNode;
  isProcessing?: boolean;
  processingLabel?: string | ReactNode;
  description?: string;
  loader: (file: any) => Promise<LoaderResult<unknown>>;
  errors?: FieldError;
  defaultFilename?: string;
  fileExtraInformation?: Array<string>;
  dropzoneClassname?: string;
  hideSelectedFile?: boolean;
  multiple?: boolean;
  bottomSlot?: ReactNode;
};

export const FileInput = ({
  onLoad,
  onError,
  onClear,
  validate,
  accept,
  label,
  loadingLabel,
  isProcessing,
  processingLabel,
  description,
  loader,
  errors,
  defaultFilename,
  fileExtraInformation: extraInformation,
  dropzoneClassname,
  hideSelectedFile,
  multiple,
  bottomSlot,
}: FileInputProps) => {
  const { t } = useTranslation();
  const [filenames, setFilenames] = useState(() => (defaultFilename ? [defaultFilename] : []));
  const [isLoading, setIsLoading] = useState(false);

  const onLoadComplete = useCallback(
    (file: File) => (results: LoaderResult<unknown>) => {
      const validData = validate ? validate(results.data) : { success: true };
      if (!validData?.success) {
        if ('errorCode' in validData) {
          onError(new Error(validData.errorCode), file);
        } else {
          onError(new Error(FileInputErrorCodes.INVALID_FILE_TYPE), file);
        }
      } else {
        onLoad(results.data, file);
        if (multiple) {
          setFilenames((currentFileNames) => [...currentFileNames, file.name]);
        } else {
          setFilenames([file.name]);
        }
      }
    },
    [multiple, onError, onLoad, validate],
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: Array<File>) => {
      setIsLoading(true);
      Promise.all(acceptedFiles.map(loader))
        .then((data) => {
          data.forEach((data, index) => {
            onLoadComplete(acceptedFiles[index])(data);
          });
        })
        .catch((error) => {
          onError(error, acceptedFiles[0]);
        })
        .finally(() => setIsLoading(false));
    },
    [onLoadComplete, loader, onError],
  );

  const onDropRejected = useCallback(
    (fileRejections: Array<FileRejection>) => {
      onError(new Error(fileRejections[0].errors[0].code), fileRejections[0].file);
    },
    [onError],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    disabled: isLoading || isProcessing,
    multiple,
    accept,
    noClick: true,
    noKeyboard: true,
    useFsAccessApi: true,
  });

  const deleteFile = (index: number) => {
    setFilenames((currentFileNames) => currentFileNames.filter((_, i) => i !== index));
    onClear?.();
  };

  return (
    <div {...getRootProps()} className="flex h-full flex-col">
      <Input {...getInputProps()} label={label} />
      <div
        className={classNames(
          'border-dashed border-2 rounded-md py-8 relative min-h-32',
          classes[errors ? 'error' : 'default'],
          isDragActive ? classes.dragActive : null,
          isLoading || isProcessing ? classes.loading : null,
          dropzoneClassname,
        )}
      >
        {isLoading || isProcessing ? (
          <>
            {/* Loading logo positioned so its bottom edge is at page center */}
            {/* This prevents jumping when switching between loading/processing states since labels can have different height */}
            <div
              className={classNames(
                'absolute left-1/2 top-1/2 -translate-x-1/2',
                processingLabel || loadingLabel ? '-translate-y-full' : '-translate-y-1/2',
              )}
            >
              <div className="size-8">
                <LoadingLogo />
              </div>
            </div>
            {/* Labels appear below the centered logo with consistent spacing */}
            {/* loadingLabel takes priority when both states are active */}
            {(isLoading && loadingLabel) || (isProcessing && processingLabel) ? (
              <div className="absolute left-1/2 top-1/2 w-max -translate-x-1/2 translate-y-3 text-center text-sm text-gray-500">
                <p>{(isLoading && loadingLabel) || processingLabel}</p>
              </div>
            ) : null}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-sm text-gray-500">
            <div className="flex flex-col items-center">
              <CloudArrowUpIcon className="h-9 w-11 stroke-gray-400" />
              <p className="text-sm font-medium text-gray-600">
                <Button variant="text" onClick={open} analyticsId="upload_file_picker_open">
                  {t('common:components.fileInput.text1')}
                </Button>{' '}
                {t('common:components.fileInput.text2')}
              </p>
              {description && <p className="text-sm text-gray-500">{description}</p>}
            </div>
          </div>
        )}
        {bottomSlot && <div className="absolute bottom-6 left-1/2 -translate-x-1/2">{bottomSlot}</div>}
      </div>
      {!!errors && <p className="mt-2 text-sm text-red-600">{errors.message}</p>}
      {filenames.length && !hideSelectedFile
        ? filenames.map((filename, index) => (
            <div className="mt-2 rounded-md border border-gray-200 px-2 py-3" key={filename + index}>
              <div className="flex items-center gap-2">
                <DocumentIcon className="h-4 w-6 stroke-gray-400" />
                <div className="flex w-full items-center justify-between gap-2 text-sm text-gray-900">
                  <p>{filename}</p>
                  <Button
                    variant="minimal"
                    size="extra-small"
                    onClick={() => deleteFile(index)}
                    analyticsId="upload_file_reset"
                  >
                    <TrashIcon className="h-4 w-6 stroke-gray-600" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2 px-8">
                {extraInformation?.map((info) => (
                  <p key={info} className="text-xs text-gray-600">
                    {info}
                  </p>
                ))}
              </div>
            </div>
          ))
        : null}
    </div>
  );
};
